"use client";

import { motion } from "framer-motion";
import {
  Users,
  Clock,
  KanbanSquare,
  Database,
} from "lucide-react";
import { fadeUp, scaleIn, Section, img, agents } from "./utils";

// Agent positions for the network graph (percentages)
const agentNodes = [
  { x: 18, y: 30 },  // Sara — left
  { x: 55, y: 18 },  // Omar — top center
  { x: 72, y: 60 },  // Layla — right
  { x: 30, y: 72 },  // Raj — bottom left
];

// Connection pairs (indices into agentNodes)
const connections: [number, number][] = [
  [0, 1], // Sara → Omar
  [1, 2], // Omar → Layla
  [0, 3], // Sara → Raj
  [2, 3], // Layla → Raj
  [0, 2], // Sara → Layla
  [1, 3], // Omar → Raj
];

const pipelineStages = ["New Lead", "Qualified", "Viewing", "Closed"];
const pipelineColors = ["#A1A1A1", "#3B82F6", "#DE7356", "#22C55E"];

function PipelineAnimation() {
  return (
    <div className="relative w-full h-36">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 100" preserveAspectRatio="xMidYMid meet">
        {/* Stage columns */}
        {pipelineStages.map((stage, i) => {
          const x = 20 + i * 70;
          return (
            <g key={stage}>
              {/* Column header */}
              <text x={x + 25} y={14} textAnchor="middle" fill="#6B6B6B" fontSize="7" fontFamily="system-ui">
                {stage}
              </text>
              {/* Column line */}
              <line x1={x + 25} y1={20} x2={x + 25} y2={92} stroke="#3D3D3D" strokeWidth="0.5" strokeDasharray="2 2" />
              {/* Stage dot */}
              <motion.circle
                cx={x + 25}
                cy={20}
                r="3"
                fill={pipelineColors[i]}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3 + i * 0.15 }}
              />
            </g>
          );
        })}

        {/* Animated deal cards moving through pipeline */}
        {[0, 1, 2].map((deal) => {
          const y = 35 + deal * 22;
          return (
            <motion.g key={deal}>
              <motion.rect
                y={y}
                width="44"
                height="16"
                rx="4"
                fill="#2D2D2D"
                stroke="#3D3D3D"
                strokeWidth="0.5"
                initial={{ x: 3, opacity: 0 }}
                animate={{
                  x: [3, 73, 143, 213],
                  opacity: [0, 1, 1, 1],
                }}
                transition={{
                  duration: 6,
                  delay: 1 + deal * 1.5,
                  repeat: Infinity,
                  repeatDelay: 4,
                  ease: "easeInOut",
                  times: [0, 0.3, 0.6, 1],
                }}
              />
              <motion.rect
                y={y + 4}
                width="28"
                height="3"
                rx="1.5"
                fill="#4A4A4A"
                initial={{ x: 11, opacity: 0 }}
                animate={{
                  x: [11, 81, 151, 221],
                  opacity: [0, 0.8, 0.8, 0.8],
                }}
                transition={{
                  duration: 6,
                  delay: 1 + deal * 1.5,
                  repeat: Infinity,
                  repeatDelay: 4,
                  ease: "easeInOut",
                  times: [0, 0.3, 0.6, 1],
                }}
              />
              <motion.rect
                y={y + 9}
                width="18"
                height="2"
                rx="1"
                initial={{ x: 11, opacity: 0 }}
                animate={{
                  x: [11, 81, 151, 221],
                  opacity: [0, 0.5, 0.5, 0.5],
                  fill: ["#6B6B6B", "#3B82F6", "#DE7356", "#22C55E"],
                }}
                transition={{
                  duration: 6,
                  delay: 1 + deal * 1.5,
                  repeat: Infinity,
                  repeatDelay: 4,
                  ease: "easeInOut",
                  times: [0, 0.3, 0.6, 1],
                }}
              />
            </motion.g>
          );
        })}

        {/* Arrow indicators between stages */}
        {[0, 1, 2].map((i) => {
          const x = 55 + i * 70;
          return (
            <motion.path
              key={`arrow-${i}`}
              d={`M${x} 50 L${x + 8} 50 L${x + 5} 47 M${x + 8} 50 L${x + 5} 53`}
              stroke="#DE7356"
              strokeWidth="0.8"
              fill="none"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.6, 0] }}
              transition={{ duration: 2, delay: 1.5 + i * 0.5, repeat: Infinity, repeatDelay: 3 }}
            />
          );
        })}
      </svg>
    </div>
  );
}

const dataFiles = [
  { name: "listings.csv", color: "#22C55E", delay: 0 },
  { name: "fleet-data.xlsx", color: "#3B82F6", delay: 0.6 },
  { name: "contracts.pdf", color: "#DE7356", delay: 1.2 },
  { name: "inventory.json", color: "#A78BFA", delay: 1.8 },
];

function DataAnimation() {
  return (
    <div className="relative w-full h-44">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 280 120" preserveAspectRatio="xMidYMid meet">
        {/* Central brain/core node */}
        <motion.circle
          cx="140"
          cy="60"
          r="18"
          fill="#2D2D2D"
          stroke="#A78BFA"
          strokeWidth="1"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.3 }}
        />
        <motion.text
          x="140"
          y="57"
          textAnchor="middle"
          fill="#A78BFA"
          fontSize="10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          AI
        </motion.text>
        <motion.text
          x="140"
          y="67"
          textAnchor="middle"
          fill="#6B6B6B"
          fontSize="6"
          fontFamily="system-ui"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          Your Data
        </motion.text>

        {/* Pulsing ring around core */}
        <motion.circle
          cx="140"
          cy="60"
          r="18"
          fill="none"
          stroke="#A78BFA"
          strokeWidth="0.5"
          animate={{
            r: [18, 26, 18],
            opacity: [0.4, 0, 0.4],
          }}
          transition={{ duration: 3, repeat: Infinity }}
        />

        {/* Data file nodes flowing in */}
        {dataFiles.map((file, i) => {
          const angle = (i * Math.PI * 2) / dataFiles.length - Math.PI / 2;
          const startX = 140 + Math.cos(angle) * 55;
          const startY = 60 + Math.sin(angle) * 45;
          return (
            <g key={file.name}>
              {/* Connection line */}
              <motion.line
                x1={startX}
                y1={startY}
                x2="140"
                y2="60"
                stroke={file.color}
                strokeWidth="0.5"
                strokeDasharray="3 3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.3 }}
                transition={{ delay: 0.5 + file.delay }}
              />

              {/* Data packet traveling to core */}
              <motion.circle
                r="2"
                fill={file.color}
                initial={{ cx: startX, cy: startY, opacity: 0 }}
                animate={{
                  cx: [startX, 140],
                  cy: [startY, 60],
                  opacity: [0, 0.9, 0.9, 0],
                }}
                transition={{
                  duration: 2,
                  delay: 1 + file.delay,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: "easeIn",
                }}
              />

              {/* File node */}
              <motion.g
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", delay: 0.3 + file.delay * 0.5 }}
              >
                <rect
                  x={startX - 22}
                  y={startY - 10}
                  width="44"
                  height="20"
                  rx="4"
                  fill="#232323"
                  stroke={file.color}
                  strokeWidth="0.6"
                  opacity="0.8"
                />
                <text
                  x={startX}
                  y={startY + 3}
                  textAnchor="middle"
                  fill={file.color}
                  fontSize="5.5"
                  fontFamily="monospace"
                >
                  {file.name}
                </text>
              </motion.g>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function AgentNetwork() {
  return (
    <div className="relative w-full h-48">
      {/* SVG connection lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {connections.map(([from, to], i) => (
          <motion.line
            key={i}
            x1={agentNodes[from].x}
            y1={agentNodes[from].y}
            x2={agentNodes[to].x}
            y2={agentNodes[to].y}
            stroke="rgba(222,115,86,0.2)"
            strokeWidth="0.3"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 + i * 0.15 }}
          />
        ))}
        {/* Animated pulse dots traveling along connections */}
        {connections.slice(0, 4).map(([from, to], i) => (
          <motion.circle
            key={`pulse-${i}`}
            r="0.8"
            fill="#DE7356"
            initial={{ opacity: 0 }}
            animate={{
              cx: [agentNodes[from].x, agentNodes[to].x],
              cy: [agentNodes[from].y, agentNodes[to].y],
              opacity: [0, 0.8, 0.8, 0],
            }}
            transition={{
              duration: 2.5,
              delay: 1.5 + i * 0.8,
              repeat: Infinity,
              repeatDelay: 3,
              ease: "easeInOut",
            }}
          />
        ))}
      </svg>

      {/* Agent nodes */}
      {agents.map((agent, i) => (
        <motion.div
          key={i}
          className="absolute flex items-center gap-2"
          style={{
            left: `${agentNodes[i].x}%`,
            top: `${agentNodes[i].y}%`,
            transform: "translate(-50%, -50%)",
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", delay: 0.2 + i * 0.12, stiffness: 200, damping: 20 }}
        >
          <div className="relative">
            <img
              src={agent.avatar}
              alt={agent.name}
              className="w-10 h-10 rounded-full object-cover border-2 border-surface-hover shadow-lg shadow-black/40"
            />
            {/* Active pulse ring */}
            <motion.div
              className="absolute -inset-1 rounded-full border border-coral-500/30"
              animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.5 }}
            />
          </div>
          <div className="bg-surface/80 backdrop-blur-sm rounded-lg px-2 py-1 border border-surface-hover/40 hidden sm:block">
            <p className="text-[11px] font-semibold text-content whitespace-nowrap">{agent.name}</p>
            <p className="text-[9px] text-content-secondary whitespace-nowrap">{agent.role}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export function BentoGrid() {
  return (
    <Section id="solutions" className="px-6 py-28 max-w-7xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          What you get
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-content">
          Hire a team that runs itself.
        </h2>
        <p className="mt-3 text-content-secondary max-w-xl mx-auto text-base">
          Each agent has a role, responsibilities, and the ability to
          work with the rest of the crew — just like a real employee.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Card 1 — Multi-Agent Coordination, wide */}
        <motion.div
          variants={scaleIn}
          custom={0}
          className="lg:col-span-7 group relative bg-surface-secondary/50 backdrop-blur-md border border-surface-border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-surface-hover"
        >
          <div className="p-8">
            <div className="w-12 h-12 rounded-xl bg-coral-500/10 border border-coral-500/20 flex items-center justify-center mb-5">
              <Users size={22} className="text-coral-400" />
            </div>
            <h3 className="text-lg font-semibold text-content mb-2">
              They work together — like a real team.
            </h3>
            <p className="text-sm text-content-secondary leading-relaxed max-w-md">
              Sara picks up new work, Omar handles logistics, Layla keeps clients
              in the loop, Raj watches the numbers. They hand off to each other
              without you in the middle.
            </p>
          </div>
          <div className="px-4 pb-4">
            <AgentNetwork />
          </div>
        </motion.div>

        {/* Card 2 — 24/7 Operations */}
        <motion.div
          variants={scaleIn}
          custom={1}
          className="lg:col-span-5 group relative bg-surface-secondary/50 backdrop-blur-md border border-surface-border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-surface-hover"
        >
          <div className="p-8">
            <div className="w-12 h-12 rounded-xl bg-ok/10 border border-ok/20 flex items-center justify-center mb-5">
              <Clock size={22} className="text-ok" />
            </div>
            <h3 className="text-lg font-semibold text-content mb-2">
              They don&apos;t clock out.
            </h3>
            <p className="text-sm text-content-secondary leading-relaxed">
              At midnight. On Eid. During your kid&apos;s school play.
              Your AI crew keeps working — handling tasks, updating
              your pipeline, and following up on your behalf.
            </p>
          </div>
          {/* Internal team thread mock — NOT a customer chat */}
          <div className="px-8 pb-6 space-y-2">
            <div className="bg-surface-tertiary/60 rounded-xl p-3 border border-surface-hover/30">
              <div className="flex items-center gap-2 mb-2">
                <img src={agents[0].avatar} alt="Sara" className="w-5 h-5 rounded-full object-cover" />
                <span className="text-[10px] font-medium text-content/80">Sara</span>
                <span className="text-[9px] text-content-muted">11:47 PM</span>
              </div>
              <p className="text-[11px] text-content-secondary">
                Picked up 3 new inquiries while you were offline. All qualified
                and handed to Omar for next steps.
              </p>
            </div>
            <div className="bg-surface-tertiary/60 rounded-xl p-3 border border-surface-hover/30">
              <div className="flex items-center gap-2 mb-2">
                <img src={agents[1].avatar} alt="Omar" className="w-5 h-5 rounded-full object-cover" />
                <span className="text-[10px] font-medium text-content/80">Omar</span>
                <span className="text-[9px] text-content-muted">11:52 PM</span>
              </div>
              <p className="text-[11px] text-content-secondary">
                Done. 2 viewings booked for tomorrow, docs prepped. One client
                needs your approval — flagged for morning.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Card 3 — Pipeline */}
        <motion.div
          variants={scaleIn}
          custom={2}
          className="lg:col-span-5 group relative bg-surface-secondary/50 backdrop-blur-md border border-surface-border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-surface-hover"
        >
          <div className="p-8">
            <div className="w-12 h-12 rounded-xl bg-info/10 border border-info/20 flex items-center justify-center mb-5">
              <KanbanSquare size={22} className="text-info" />
            </div>
            <h3 className="text-lg font-semibold text-content mb-2">
              Your pipeline updates itself.
            </h3>
            <p className="text-sm text-content-secondary leading-relaxed">
              Agents move deals through stages, create tasks, and log
              every interaction. You open your dashboard — it&apos;s already current.
            </p>
          </div>
          <div className="px-4 pb-4">
            <PipelineAnimation />
          </div>
        </motion.div>

        {/* Card 4 — Domain Knowledge, wide */}
        <motion.div
          variants={scaleIn}
          custom={3}
          className="lg:col-span-7 group relative bg-surface-secondary/50 backdrop-blur-md border border-surface-border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-surface-hover"
        >
          <div className="flex flex-col sm:flex-row">
            <div className="p-8 flex-1">
              <div className="w-12 h-12 rounded-xl bg-highlight/10 border border-highlight/20 flex items-center justify-center mb-5">
                <Database size={22} className="text-highlight" />
              </div>
              <h3 className="text-lg font-semibold text-content mb-2">
                Trained on your data. Not the internet.
              </h3>
              <p className="text-sm text-content-secondary leading-relaxed">
                Upload your listings, fleet specs, or service catalogs. Your
                agents reason over your business data — so they give answers
                you&apos;d actually stand behind.
              </p>
            </div>
            <div className="sm:w-64 shrink-0">
              <DataAnimation />
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
